/**
 * Profit Agent API — conversational profitability Q&A (Phase 1+).
 */
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { logAndFallback } from '../utils/userFacingError';
import type {
  ProfitAgentProgressEvent,
  ProfitAgentQueryRequest,
  ProfitAgentQueryResponse,
} from '../types/profitabilityAgent';

const PROFIT_AGENT_QUERY_URL = API_ENDPOINTS.profitabilityAgentQuery();
const PROFIT_AGENT_STREAM_URL = API_ENDPOINTS.profitabilityAgentQueryStream();
/** Local 14B + tool loop can exceed 4 min; allow 10 min before the browser aborts. */
const QUERY_TIMEOUT_MS = 600_000;

function formatProfitAgentFetchError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') {
    const mins = Math.round(QUERY_TIMEOUT_MS / 60_000);
    return (
      `The request timed out after ${mins} minutes. ` +
      'Try a shorter date range or a more specific question, then ask again.'
    );
  }
  if (err instanceof Error && /aborted without reason/i.test(err.message)) {
    const mins = Math.round(QUERY_TIMEOUT_MS / 60_000);
    return (
      `The request was cancelled after about ${mins} minutes. ` +
      'Try a shorter date range or a more specific question, then ask again.'
    );
  }
  if (err instanceof TypeError && /failed to fetch/i.test(err.message)) {
    return (
      'Could not reach Profit Agent right now. Please try again in a moment. ' +
      'If this keeps happening, contact your ZeeFleet administrator.'
    );
  }
  return logAndFallback(
    err,
    '[Profit Agent] request error:',
    'We could not complete the profit analysis right now. ' +
      'Please try again shortly, or contact your ZeeFleet administrator if this continues.',
  );
}

function parseSseBlock(block: string): { eventName: string; data: string } | null {
  let eventName = 'message';
  let data = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event: ')) eventName = line.slice(7).trim();
    else if (line.startsWith('data: ')) data = line.slice(6);
  }
  return data ? { eventName, data } : null;
}

/**
 * Stream a Profit Agent query via SSE (POST /agent/query/stream).
 * Calls `onProgress` during the ReAct loop; resolves with the final JSON result.
 */
export async function streamProfitAgentQuery(
  request: ProfitAgentQueryRequest,
  onProgress: (event: ProfitAgentProgressEvent) => void,
  signal?: AbortSignal,
): Promise<ProfitAgentQueryResponse> {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(new DOMException('Profit Agent query timeout', 'AbortError')),
    QUERY_TIMEOUT_MS,
  );

  const onExternalAbort = () => controller.abort();
  signal?.addEventListener('abort', onExternalAbort);

  try {
    const res = await authenticatedFetch(PROFIT_AGENT_STREAM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    if (!res.ok) {
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(json.error || `Profit Agent stream failed (${res.status})`);
    }

    if (!res.body) {
      throw new Error('No response body for SSE stream');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const parsed = parseSseBlock(block);
        if (!parsed) continue;

        if (parsed.eventName === 'progress') {
          try {
            const event = JSON.parse(parsed.data) as ProfitAgentProgressEvent;
            onProgress(event);
          } catch {
            // ignore malformed progress events
          }
        } else if (parsed.eventName === 'result') {
          return JSON.parse(parsed.data) as ProfitAgentQueryResponse;
        } else if (parsed.eventName === 'error') {
          const err = JSON.parse(parsed.data) as { message: string };
          throw new Error(err.message || 'Profit Agent stream error');
        }
      }
    }

    throw new Error('SSE stream ended without a result event');
  } catch (err) {
    throw new Error(formatProfitAgentFetchError(err));
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener('abort', onExternalAbort);
  }
}

export async function submitProfitAgentQuery(
  request: ProfitAgentQueryRequest,
): Promise<ProfitAgentQueryResponse> {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(new DOMException('Profit Agent query timeout', 'AbortError')),
    QUERY_TIMEOUT_MS,
  );

  try {
    const res = await authenticatedFetch(PROFIT_AGENT_QUERY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    const json = (await res.json().catch(() => ({}))) as ProfitAgentQueryResponse & { error?: string };

    if (!res.ok) {
      throw new Error(json.error || json.summary || `Profit Agent request failed (${res.status})`);
    }

    return json;
  } catch (err) {
    throw new Error(formatProfitAgentFetchError(err));
  } finally {
    window.clearTimeout(timeout);
  }
}
